// Types and labels for the Agreements page and the driver's agreements panel.
// db/migrations/3700 to 3707, docs/AGREEMENTS.md. Every figure is computed in
// Postgres; this file only names things.
import type { ChipTone } from '../components/interviews/Chip'

export type AddendumStatus = 'awaiting_induction' | 'ready_to_sign' | 'signed' | 'declined'

export type AddendumRow = {
  id: string
  driver_id: string
  person_id: string
  driver_name: string | null
  status: AddendumStatus
  amends_version: string
  amends_signed_on: string | null
  attended_at: string | null
  session_starts_at: string | null
  session_venue: string | null
  envelope_id: string | null
  envelope_sent_at: string | null
  envelope_error: string | null
  signed_on: string | null
  has_signed_pdf: boolean
  declined_on: string | null
  decline_note: string | null
  decline_source: 'manager' | 'signnow' | null
  invitation_status: string | null
  invitation_sent_at: string | null
  invitation_failure: string | null
}

export type SessionPerson = { person_id: string; name: string | null; status: string; marked_at: string | null }

export type InductionSession = {
  id: string
  session_type: 'new_driver' | 'addendum_1'
  starts_at: string | null
  session_date: string
  venue: string | null
  status: string
  pack_version: string | null
  capacity: number | null
  people: SessionPerson[]
}

export type AgreementDocument = {
  id: string
  document_set: string
  kind: string
  title: string
  sha256: string
  bytes: number
  recorded_at: string
}

export type AgreementsPage = {
  can_record: boolean
  is_admin: boolean
  current: { version: string; in_force_from: string; template_name: string } | null
  versions: { version: string; in_force_from: string | null; is_addendum: boolean; template_built: boolean }[]
  counts: {
    v6_issued: number
    v6_signed: number
    addendum_awaiting_induction: number
    addendum_ready_to_sign: number
    addendum_signed: number
    addendum_declined: number
    older_agreements: number
  }
  v6_contracts: { envelope_id: string; person_id: string; name: string | null; status: string; sent_at: string | null; signed_at: string | null; kind: string }[]
  addenda: AddendumRow[]
  older: { driver_id: string; person_id: string; name: string | null; version: string; signed_on: string | null }[]
  sessions: InductionSession[]
  documents: AgreementDocument[]
  invitations: { queued: number; sent: number; failed: number; not_sent: number }
}

export type Bookable = { person_id: string; name: string | null; stage: string; has_email: boolean }

export type InvitationPreview = {
  ok: boolean
  reason?: string
  session: { session_date: string; session_time: string; session_venue: string }
  samples: { driver_name: string; to: string; subject: string; body_html: string; body_text: string; template_version: number }[]
  attachments: { title: string; filename: string; bytes: number; sha256: string }[]
  recipients: { addendum_id: string; driver_name: string; email: string | null; status: string; sendable: boolean; reason: string | null }[]
  will_send: number
}

export type DriverAgreements = {
  terms: {
    terms: string
    agreement_version: string | null
    agreement_signed_on: string | null
    addendum_status: AddendumStatus | null
    addendum_signed_on: string | null
    addendum_terms_from: string | null
    addendum_terms_apply: boolean
  } | null
  contracts: { envelope_id: string; status: string; version: string | null; sent_at: string | null; signed_at: string | null }[]
  signatures: { version: string; signed_on: string; evidence: string | null }[]
  addendum: {
    id: string
    status: AddendumStatus
    amends_version: string
    attended_at: string | null
    envelope_sent_at: string | null
    envelope_error: string | null
    signed_on: string | null
    has_signed_pdf: boolean
    declined_on: string | null
    decline_note: string | null
    invitation_status: string | null
    events: { event: string; to_status: string | null; note: string | null; at: string }[]
  } | null
  documents: { id: string; title: string; kind: string; document_set: string }[]
  pack: { pack_version: string; sent: boolean; note: string | null; message_status: string | null } | null
}

export const ADDENDUM_STATUS: Record<AddendumStatus, { label: string; tone: ChipTone }> = {
  awaiting_induction: { label: 'Awaiting induction', tone: 'neutral' },
  ready_to_sign: { label: 'Ready to sign', tone: 'brand' },
  signed: { label: 'Signed', tone: 'green' },
  declined: { label: 'Declined', tone: 'amber' },
}

export const SESSION_TYPE_LABEL: Record<InductionSession['session_type'], string> = {
  new_driver: 'New-driver induction',
  addendum_1: 'Addendum No. 1 session',
}

export const ATTENDANCE_LABEL: Record<string, string> = {
  invited: 'Booked',
  attended: 'Present',
  absent: 'Absent',
  excused: 'Excused',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled',
}

export const MESSAGE_STATUS_LABEL: Record<string, string> = {
  queued: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  held: 'Held',
}

export const DOCUMENT_SET_LABEL: Record<string, string> = {
  v6: 'v6 agreement',
  v6_signing: 'v6 as signed',
  addendum_1: 'Addendum No. 1',
  addendum_1_signing: 'Addendum No. 1 as signed',
  'induction_2026-10': 'Induction pack 2026-10',
}

// A date and a SAST time typed into the session form, as an ISO instant.
// South Africa has no daylight saving, so +02:00 is always right.
export function sastInstant(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null
  return `${date}T${time}:00+02:00`
}

export function shortHash(sha: string): string {
  return sha ? `${sha.slice(0, 12)}…` : '—'
}

export function kb(bytes: number): string {
  return bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`
}
